<template>
  <div class="pp" :class="{ 'pp--chip': chip }">
    <!-- chip variant: ultra-compact, for the conversation's pinned summary bar -->
    <button v-if="chip" type="button" class="pp-chip" @click="dialog = true">
      <span class="pp-chip__thumb">
        <img v-if="cover" :src="cover" alt="" />
        <hero-icon v-else :icon="HomeIcon" :size="13" />
      </span>
      <span class="pp-chip__text">
        <span class="pp-chip__addr">{{ shortAddress }}</span>
        <span v-if="stripMeta" class="pp-chip__meta">{{ stripMeta }}</span>
      </span>
    </button>

    <!-- compact strip — opens the preview dialog -->
    <button v-else type="button" class="pp-strip" @click="dialog = true">
      <span class="pp-strip__thumb" :class="{ 'pp-strip__thumb--empty': !cover }">
        <img v-if="cover" :src="cover" alt="" />
        <hero-icon v-else :icon="HomeIcon" :size="16" />
      </span>
      <span class="pp-strip__text">
        <span class="pp-strip__addr">{{ property.fullAddress }}</span>
        <span v-if="stripMeta" class="pp-strip__meta">{{ stripMeta }}</span>
      </span>
      <hero-icon :icon="ArrowIcon" :size="15" class="pp-strip__go" />
    </button>

    <!-- preview dialog -->
    <v-dialog v-model="dialog" max-width="560" :fullscreen="$vuetify.breakpoint.smAndDown" content-class="pp-dialog">
      <div class="pp-card">
        <div class="pp-card__media" :class="{ 'pp-card__media--empty': !cover }">
          <img v-if="cover" :src="cover" alt="" />
          <hero-icon v-else :icon="HomeIcon" :size="40" />
          <button
            type="button"
            class="pp-card__close"
            @click="dialog = false"
            aria-label="Cerrar"
          >
            <hero-icon :icon="XMarkIcon" :size="18" />
          </button>
        </div>

        <div class="pp-card__body">
          <div class="pp-card__addr">{{ property.fullAddress }}</div>

          <div v-if="typeLabel || operationLabels.length" class="pp-card__tags">
            <span v-if="typeLabel" class="pp-tag">{{ typeLabel }}</span>
            <span v-for="op in operationLabels" :key="op" class="pp-tag pp-tag--op">{{ op }}</span>
          </div>

          <div v-if="prices.length" class="pp-card__prices">
            <div v-for="p in prices" :key="p.label" class="pp-price">
              <span class="pp-price__label">{{ p.label }}</span>
              <span class="pp-price__value">{{ p.value }}</span>
            </div>
          </div>

          <p v-if="property.description" class="pp-card__desc">{{ property.description }}</p>
        </div>

        <div class="pp-card__actions">
          <v-btn text color="primary" class="rounded-lg" @click="dialog = false">
            Cerrar
          </v-btn>
          <v-btn color="accent" depressed class="rounded-lg" @click="openProperty">
            Abrir propiedad
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import HeroIcon from "@/components/shared/HeroIcon";
import HomeIcon from "heroicons/24/outline/home.svg";
import ArrowIcon from "heroicons/24/outline/arrow-up-right.svg";
import XMarkIcon from "heroicons/24/outline/x-mark.svg";

const OP_LABELS = { sell: "Venta", rent: "Alquiler" };

export default {
  name: "PropertyPreview",

  components: { HeroIcon },

  props: {
    property: {
      type: Object,
      required: true
    },
    chip: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    dialog: false,
    HomeIcon,
    ArrowIcon,
    XMarkIcon
  }),

  computed: {
    cover() {
      const imgs = this.property.images || [];
      if (!imgs.length) return null;
      const path = imgs[0].smallUrl || imgs[0].thumbUrl || imgs[0].url;
      return path ? process.env.VUE_APP_RAILS_URL + path : null;
    },

    typeLabel() {
      const t = this.property.propertyType;
      return t ? this.$t("property.type." + t.toLowerCase()) : "";
    },

    operationLabels() {
      return (this.property.operationTypes || []).map(o => OP_LABELS[o] || o);
    },

    prices() {
      const p = this.property;
      const ops = p.operationTypes || [];
      const out = [];
      if ((ops.includes("sell") || !ops.length) && p.sellPrice)
        out.push({ label: "Venta", value: this.money(p.sellCurrency, p.sellPrice) });
      if ((ops.includes("rent") || !ops.length) && p.price)
        out.push({ label: "Alquiler", value: this.money(p.currency, p.price) });
      return out;
    },

    stripMeta() {
      const bits = [];
      if (this.typeLabel) bits.push(this.typeLabel);
      if (this.prices.length) bits.push(this.prices[0].value);
      return bits.join(" · ");
    },

    // street + number only — the chip is too small for city/state
    shortAddress() {
      return (this.property.fullAddress || "").split(",")[0];
    }
  },

  methods: {
    money(currency, value) {
      const n = Number(value);
      const num = isNaN(n) ? value : n.toLocaleString("es-AR");
      return `${currency || ""} ${num}`.trim();
    },

    openProperty() {
      const path = this.$router.resolve({
        name: "Property",
        params: { id: this.property.id }
      });
      window.open(path.href, "_blank");
    }
  }
};
</script>

<style scoped>
/* chip variant (conversation summary bar) */
.pp--chip { display: inline-flex; min-width: 0; }
.pp-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 10px;
  padding: 5px 10px 5px 5px;
  cursor: pointer;
  max-width: 250px;
  min-width: 0;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.pp-chip:hover { border-color: #dcd4c6; box-shadow: 0 4px 12px -6px rgba(40, 33, 22, 0.25); }
.pp-chip__thumb {
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 7px;
  overflow: hidden;
  background: #ece7de;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a39a8c;
}
.pp-chip__thumb img { width: 100%; height: 100%; object-fit: cover; }
.pp-chip__text { min-width: 0; display: flex; flex-direction: column; text-align: left; }
.pp-chip__addr {
  font-size: 12px;
  font-weight: 600;
  color: #1a1814;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pp-chip__meta {
  font-size: 10.5px;
  color: #756f64;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* compact strip */
.pp-strip {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  background: #faf8f4;
  border: 1px solid #ece7de;
  border-radius: 12px;
  padding: 10px 12px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.pp-strip:hover {
  border-color: #dcd4c6;
  background: #f5f1ea;
}
.pp-strip__thumb {
  flex: none;
  width: 42px;
  height: 42px;
  border-radius: 9px;
  overflow: hidden;
  background: #ece7de;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a39a8c;
}
.pp-strip__thumb img { width: 100%; height: 100%; object-fit: cover; }
.pp-strip__text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.pp-strip__addr {
  font-size: 13.5px;
  font-weight: 600;
  color: #1a1814;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pp-strip__meta {
  font-size: 12px;
  color: #756f64;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pp-strip__go { flex: none; color: #b52118; }

/* preview dialog */
.pp-card { background: #fff; border-radius: 12px; overflow: hidden; }
.pp-card__media {
  position: relative;
  height: 210px;
  background: #ece7de;
}
.pp-card__media img { width: 100%; height: 100%; object-fit: cover; display: block; }
.pp-card__media--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c4bcae;
}
.pp-card__close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(26, 24, 20, 0.55);
  transition: background 0.15s ease;
}
.pp-card__close:hover { background: rgba(26, 24, 20, 0.78); }

.pp-card__body { padding: 18px 22px 6px; }
.pp-card__addr { font-size: 17px; font-weight: 700; color: #1a1814; line-height: 1.25; }

.pp-card__tags { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 11px; }
.pp-tag {
  font-size: 11.5px;
  font-weight: 600;
  color: #6a645a;
  background: #f1ece3;
  border-radius: 999px;
  padding: 3px 11px;
}
.pp-tag--op { color: #1a1814; background: #ece7de; }

.pp-card__prices { display: flex; flex-wrap: wrap; gap: 22px; margin-top: 14px; }
.pp-price { display: flex; flex-direction: column; gap: 1px; }
.pp-price__label {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #a39a8c;
  font-weight: 600;
}
.pp-price__value { font-size: 15.5px; font-weight: 700; color: #1a1814; }

.pp-card__desc {
  margin: 14px 0 0;
  font-size: 13px;
  line-height: 1.55;
  color: #56504a;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.pp-card__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 22px 18px;
}
</style>
